import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Layers, Globe, Loader2, Coins, Copy, Wand2, RefreshCw, Sparkles, CalendarClock,
  Instagram, Youtube, Facebook, Linkedin, Music2, Share2,
} from "lucide-react";

const PLATFORMS = [
  { id: "instagram", label: "Instagram",  icon: Instagram },
  { id: "tiktok",    label: "TikTok",     icon: Music2 },
  { id: "youtube",   label: "YouTube",    icon: Youtube },
  { id: "facebook",  label: "Facebook",   icon: Facebook },
  { id: "linkedin",  label: "LinkedIn",   icon: Linkedin },
  { id: "twitter",   label: "X / Twitter", icon: Share2 },
];

const LANGUAGES = [
  { id: "en", label: "English" }, { id: "nl", label: "Nederlands" },
  { id: "es", label: "Español" }, { id: "fr", label: "Français" },
  { id: "de", label: "Deutsch" }, { id: "it", label: "Italiano" }, { id: "pt", label: "Português" },
];

function nextHourISO(offsetHours = 24) {
  const d = new Date();
  d.setHours(d.getHours() + offsetHours, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

export default function AISeries() {
  const { isOwner, refreshBalance } = useAuth();
  const nav = useNavigate();

  // ---- Brand profile state ----
  const [profile, setProfile] = useState({
    website_url: "", website_summary: "", social_handles: {}, reference_posts: [], brand_summary: "",
  });
  const [scraping, setScraping] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // ---- Series config ----
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(7);
  const [platform, setPlatform] = useState("instagram");
  const [tone, setTone] = useState("engaging");
  const [language, setLanguage] = useState("en");
  const [niche, setNiche] = useState("general");
  const [useProfile, setUseProfile] = useState(true);

  const [series, setSeries] = useState(null);
  const [generating, setGenerating] = useState(false);

  // ---- Bulk schedule modal state ----
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [startAt, setStartAt] = useState(nextHourISO(24));
  const [hoursApart, setHoursApart] = useState(24);
  const [targetLangs, setTargetLangs] = useState(["en"]);
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    api.get("/users/brand-profile").then((r) => {
      setProfile(r.data);
      if (r.data.website_url) {
        // Try to guess reasonable defaults
      }
    }).catch(() => {});
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/users/brand-profile", {
        brand_summary: profile.brand_summary,
        social_handles: profile.social_handles,
        reference_posts: profile.reference_posts.filter((r) => r.trim()),
      });
      toast.success("Brand profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingProfile(false); }
  };

  const scrapeWebsite = async () => {
    if (!profile.website_url.trim()) return toast.error("Voer een URL in");
    setScraping(true);
    try {
      const { data } = await api.post("/content/series/scrape-website", { url: profile.website_url });
      toast.success(`Gescand: ${data.title || data.url}`);
      // Refresh full profile
      const { data: p } = await api.get("/users/brand-profile");
      setProfile(p);
      refreshBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape mislukt");
    } finally { setScraping(false); }
  };

  const setHandle = (k, v) => setProfile((p) => ({ ...p, social_handles: { ...(p.social_handles || {}), [k]: v } }));
  const setRefPost = (idx, v) => setProfile((p) => {
    const arr = [...(p.reference_posts || [])];
    arr[idx] = v;
    return { ...p, reference_posts: arr };
  });
  const addRefPost = () => setProfile((p) => ({ ...p, reference_posts: [...(p.reference_posts || []), ""] }));
  const removeRefPost = (idx) => setProfile((p) => ({
    ...p, reference_posts: (p.reference_posts || []).filter((_, i) => i !== idx),
  }));

  const generate = async () => {
    if (!topic.trim()) return toast.error("Geef een series onderwerp");
    setGenerating(true);
    try {
      const { data } = await api.post("/content/series/generate", {
        topic, count: Number(count), platform, tone, language, niche, use_brand_profile: useProfile,
      });
      setSeries(data);
      toast.success(`${data.posts.length} posts gegenereerd${data.credits_used ? ` · ${data.credits_used} credits` : ""}`);
      refreshBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Series mislukt");
    } finally { setGenerating(false); }
  };

  const toggleLang = (id) => setTargetLangs((cur) =>
    cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const bulkSchedule = async () => {
    if (!series?.posts?.length) return;
    if (!targetLangs.length) return toast.error("Kies minstens één taal");
    setScheduling(true);
    try {
      const { data } = await api.post("/content/series/bulk-schedule", {
        platform, posts: series.posts,
        start_at: new Date(startAt).toISOString(),
        hours_apart: Number(hoursApart),
        source_language: language,
        target_languages: targetLangs,
      });
      toast.success(`${data.created_count} items ingepland · ${data.translations_used} vertalingen`);
      setScheduleOpen(false);
      refreshBalance();
      nav("/calendar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plannen mislukt");
    } finally { setScheduling(false); }
  };

  const copyPost = async (p) => {
    const tags = (p.hashtags || []).map((h) => `#${h}`).join(" ");
    const full = [p.title, "", p.body, "", tags].filter(Boolean).join("\n");
    await navigator.clipboard.writeText(full);
    toast.success("Gekopieerd");
  };

  const totalCost = count * 3;

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1500px]" data-testid="ai-series-page">
      <div className="mb-8">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">AI · CONTENT · SERIES</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          30 posts. <span className="font-bold text-amber-400">Één klik.</span>
        </h1>
        <p className="text-zinc-400 mt-3 max-w-2xl text-sm">
          Geef één idee, wij genereren een complete content-serie in jouw stem — geplaatst op je kalender, optioneel vertaald naar meerdere talen.
        </p>
      </div>

      {/* Brand profile card */}
      <section className="bg-[#121214] border border-white/5 rounded-lg p-6 mb-6" data-testid="brand-profile-section">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
          <div className="font-heading font-bold tracking-tight">Brand voice profile</div>
          <span className="ml-auto font-mono-tech text-[10px] text-zinc-500">
            AI leert jouw stem uit deze bronnen
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Website scrape */}
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500 mb-2 block">
              WEBSITE · WORDT · GESCAND
            </Label>
            <div className="flex gap-2">
              <Input
                value={profile.website_url}
                onChange={(e) => setProfile((p) => ({ ...p, website_url: e.target.value }))}
                placeholder="https://jouwsite.com"
                data-testid="brand-website"
                className="bg-[#0b0b0d] border-white/10 h-11"
              />
              <Button
                onClick={scrapeWebsite}
                disabled={scraping || !profile.website_url.trim()}
                data-testid="brand-scrape"
                className="bg-amber-500 hover:bg-amber-600 text-black h-11 whitespace-nowrap"
              >
                {scraping ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                Scan {!isOwner && <span className="ml-1 opacity-75">· 1</span>}
              </Button>
            </div>
            {profile.website_summary && (
              <div className="mt-2 text-[10px] text-emerald-400 font-mono-tech flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3" strokeWidth={1.5} />
                {profile.website_summary.length} chars opgeslagen
              </div>
            )}
          </div>

          {/* Brand summary */}
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500 mb-2 block">
              WIE · BEN · JIJ · EN · WAT · DOE · JIJ
            </Label>
            <Input
              value={profile.brand_summary}
              onChange={(e) => setProfile((p) => ({ ...p, brand_summary: e.target.value }))}
              placeholder="Bijv: I post about vegan cooking + gym lifestyle"
              data-testid="brand-summary"
              className="bg-[#0b0b0d] border-white/10 h-11"
            />
          </div>

          {/* Social handles */}
          <div className="lg:col-span-2">
            <Label className="font-mono-tech text-[10px] text-zinc-500 mb-2 block">
              SOCIAL · HANDLES · (OPTIONEEL)
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {["instagram", "tiktok", "youtube", "linkedin"].map((k) => (
                <Input
                  key={k}
                  value={(profile.social_handles || {})[k] || ""}
                  onChange={(e) => setHandle(k, e.target.value)}
                  placeholder={`@${k}-handle`}
                  data-testid={`brand-handle-${k}`}
                  className="bg-[#0b0b0d] border-white/10 h-11 text-sm"
                />
              ))}
            </div>
          </div>

          {/* Reference posts */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-mono-tech text-[10px] text-zinc-500">
                VOORBEELD · POSTS · (VOICE · REFERENCE)
              </Label>
              <button
                onClick={addRefPost}
                data-testid="brand-add-ref"
                className="text-[10px] font-mono-tech text-amber-400 hover:text-amber-300"
              >
                + Voorbeeld toevoegen
              </button>
            </div>
            <div className="space-y-2">
              {(profile.reference_posts || []).map((r, i) => (
                <div key={i} className="flex gap-2">
                  <Textarea
                    value={r}
                    onChange={(e) => setRefPost(i, e.target.value)}
                    placeholder="Plak hier een oude post of caption die jouw stijl vertegenwoordigt…"
                    rows={2}
                    data-testid={`brand-ref-${i}`}
                    className="bg-[#0b0b0d] border-white/10 text-sm flex-1"
                  />
                  <Button
                    onClick={() => removeRefPost(i)}
                    variant="outline"
                    size="icon"
                    className="bg-transparent border-white/10 hover:bg-red-500/10 hover:text-red-400 h-auto"
                  >
                    ×
                  </Button>
                </div>
              ))}
              {(!profile.reference_posts || profile.reference_posts.length === 0) && (
                <div className="text-xs text-zinc-500 italic">
                  Nog geen voorbeelden. Voeg er 2-3 toe voor de beste stemmatch.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end">
          <Button
            onClick={saveProfile}
            disabled={savingProfile}
            data-testid="brand-save"
            variant="outline"
            className="bg-transparent border-white/15 hover:bg-white/5"
          >
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Profiel opslaan
          </Button>
        </div>
      </section>

      {/* Series config */}
      <section className="bg-[#121214] border border-white/5 rounded-lg p-6 mb-6" data-testid="series-config-section">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
          <div className="font-heading font-bold tracking-tight">Series configureren</div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="font-mono-tech text-[10px] text-zinc-500">SERIES · ONDERWERP *</Label>
            <Textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Bijv: '30 dagen makelaars-tips voor eerste-huizenkopers', 'Achter de schermen van een café-lancering', 'Home workouts voor drukbezette ouders'"
              rows={2}
              data-testid="series-topic"
              className="mt-2 bg-[#0b0b0d] border-white/10"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">AANTAL · POSTS</Label>
              <Select value={String(count)} onValueChange={(v) => setCount(Number(v))}>
                <SelectTrigger data-testid="series-count" className="mt-2 bg-[#0b0b0d] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1e] border-white/10">
                  {[3, 5, 7, 10, 14, 21, 30].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} posts</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">PLATFORM</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="series-platform" className="mt-2 bg-[#0b0b0d] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1e] border-white/10">
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">TOON</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="series-tone" className="mt-2 bg-[#0b0b0d] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1e] border-white/10">
                  <SelectItem value="engaging">Engaging</SelectItem>
                  <SelectItem value="funny">Grappig</SelectItem>
                  <SelectItem value="professional">Professioneel</SelectItem>
                  <SelectItem value="luxury">Luxe</SelectItem>
                  <SelectItem value="edgy">Edgy</SelectItem>
                  <SelectItem value="informative">Informatief</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">TAAL · BRON</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="series-lang" className="mt-2 bg-[#0b0b0d] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1e] border-white/10">
                  {LANGUAGES.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">NICHE</Label>
              <Input
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                placeholder="fashion, tech…"
                data-testid="series-niche"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={useProfile}
              onChange={(e) => setUseProfile(e.target.checked)}
              data-testid="series-use-profile"
              className="accent-amber-500"
            />
            Gebruik mijn brand profile hierboven als stemreferentie
          </label>

          <Button
            onClick={generate}
            disabled={generating || !topic.trim()}
            data-testid="series-generate"
            className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Genereer {count} posts
            {!isOwner && (
              <span className="ml-2 text-[10px] font-mono-tech opacity-75 flex items-center gap-1">
                <Coins className="w-3 h-3" /> {totalCost}
              </span>
            )}
          </Button>
        </div>
      </section>

      {/* Results */}
      {series && (
        <section className="space-y-4" data-testid="series-results">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono-tech text-[10px] text-amber-400 mb-1">SERIES · READY</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">{series.series_title}</h2>
            </div>
            <Button
              onClick={() => { setTargetLangs([language]); setScheduleOpen(true); }}
              data-testid="series-schedule-all"
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
            >
              <CalendarClock className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Plan alle {series.posts.length} in kalender
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {series.posts.map((p, i) => (
              <div key={i} data-testid={`series-post-${i}`}
                className="bg-[#121214] border border-white/5 rounded-lg p-5 hover:border-white/15 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-amber-500/20 text-amber-400 flex items-center justify-center font-mono-tech text-[10px] font-bold">
                      {p.day || i + 1}
                    </div>
                    {p.angle && (
                      <span className="font-mono-tech text-[9px] text-zinc-400 uppercase">
                        {p.angle}
                      </span>
                    )}
                  </div>
                  <button onClick={() => copyPost(p)}
                    className="text-zinc-500 hover:text-amber-400" title="Copy">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                {p.title && (
                  <div className="font-heading font-bold text-sm tracking-tight mb-2">{p.title}</div>
                )}
                <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap mb-3">{p.body}</p>
                {p.cta && (
                  <div className="text-xs text-amber-400 mb-2">→ {p.cta}</div>
                )}
                {Array.isArray(p.hashtags) && p.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {p.hashtags.map((h, j) => (
                      <span key={j} className="text-[10px] text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">
                        #{typeof h === "string" ? h.replace(/^#/, "") : h}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bulk schedule modal */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-lg" data-testid="series-schedule-modal">
          <DialogHeader>
            <div className="font-mono-tech text-[10px] text-amber-400 mb-1">PLAN · SERIES</div>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              Plan de hele serie in
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Elke post wordt ingepland op {platform.toUpperCase()}. Kies talen (extra talen = automatische vertaling, 2 credits per vertaling).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500 mb-2 block">
                TALEN · (BRON: {language.toUpperCase()})
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {LANGUAGES.map((l) => {
                  const active = targetLangs.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      onClick={() => toggleLang(l.id)}
                      data-testid={`series-lang-${l.id}`}
                      className={`text-xs rounded-md border px-2 py-2 transition-colors ${
                        active
                          ? "border-amber-500 bg-amber-500/10 text-amber-300"
                          : "border-white/10 bg-[#0b0b0d] text-zinc-400 hover:border-white/25"
                      }`}
                    >
                      {l.id.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">START · DATUM · TIJD</Label>
                <Input
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  data-testid="series-schedule-start"
                  className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
                />
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">INTERVAL</Label>
                <Select value={String(hoursApart)} onValueChange={(v) => setHoursApart(Number(v))}>
                  <SelectTrigger data-testid="series-schedule-interval" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="4">Elke 4 uur</SelectItem>
                    <SelectItem value="12">Elke 12 uur</SelectItem>
                    <SelectItem value="24">Elke dag</SelectItem>
                    <SelectItem value="48">Elke 2 dagen</SelectItem>
                    <SelectItem value="72">Elke 3 dagen</SelectItem>
                    <SelectItem value="168">Elke week</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-xs text-zinc-500 p-3 bg-amber-500/[0.05] border border-amber-500/20 rounded">
              → Er worden {(series?.posts.length || 0) * targetLangs.length} items ingepland.
              {targetLangs.filter((l) => l !== language).length > 0 && (
                <> Vertalingen: {(series?.posts.length || 0) * targetLangs.filter((l) => l !== language).length} × 2 credits.</>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setScheduleOpen(false)}
              disabled={scheduling}
              className="bg-transparent border-white/15 hover:bg-white/5"
            >
              Annuleren
            </Button>
            <Button
              onClick={bulkSchedule}
              disabled={scheduling || !targetLangs.length}
              data-testid="series-schedule-confirm"
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            >
              {scheduling
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig…</>
                : "Plan alles in"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
